import { Alert, Button, Card, CardContent, EmptyState, Link, PageHeader } from '@1k5-tech-es/grado-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2, Download } from 'lucide-react';
import { useState } from 'react';

import { api } from '../../api/client';
import type { EmailOverview, QuarterSummary, ReportableQuarter, ReportRun } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/ui';
import { describeApiError } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { AdvisorPreview } from './AdvisorPreview';
import { DataReview } from './DataReview';
import { EmailPanel } from './EmailPanel';
import { planSend, sendProgress } from './emails';
import { QuarterHeading, QuarterTimeline } from './QuarterOverview';
import { QuarterPicker } from './QuarterPicker';
import { quarterLabel, samePeriod, selectQuarter, startKey } from './quarters';
import { RunHistory } from './RunHistory';
import { SendConfirmDialog } from './SendConfirmDialog';

export function ReportsPage() {
  const queryClient = useQueryClient();
  const quarters = useQuery({
    queryKey: ['quarters'],
    queryFn: () => api.get<{ quarters: ReportableQuarter[] }>('/api/reports/quarters').then((r) => r.quarters),
  });
  const runs = useQuery({
    queryKey: ['report-runs'],
    queryFn: () => api.get<{ runs: ReportRun[] }>('/api/reports').then((r) => r.runs),
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = quarters.data ? selectQuarter(quarters.data, selectedKey) : null;

  const summary = useQuery({
    queryKey: ['quarter-summary', selected?.end.year, selected?.end.month],
    queryFn: () =>
      api
        .get<{ summary: QuarterSummary }>(`/api/reports/quarters/${selected!.end.year}/${selected!.end.month}`)
        .then((r) => r.summary),
    enabled: selected !== null,
    placeholderData: keepPreviousData,
  });
  // While a new quarter loads, never show the previous quarter's figures as if they were current.
  const currentSummary = summary.data && selected && samePeriod(summary.data.end, selected.end) ? summary.data : undefined;

  const generate = useMutation({
    mutationFn: (q: ReportableQuarter) => api.post<{ run: ReportRun }>('/api/reports', q.end).then((r) => r.run),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: ['report-runs'] });
      window.location.assign(`/api/reports/${run.id}/download`);
    },
  });

  // Reports by email: the overview is refreshed every few seconds while a send is running.
  const emails = useQuery({
    queryKey: ['emails', selected?.end.year, selected?.end.month],
    queryFn: () => api.get<EmailOverview>(`/api/emails?year=${selected!.end.year}&month=${selected!.end.month}`),
    enabled: selected !== null,
    refetchInterval: (query) => (query.state.data && sendProgress(query.state.data.advisors).active ? 3_000 : false),
  });
  const refreshEmails = () => void queryClient.invalidateQueries({ queryKey: ['emails'] });
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<number[] | 'all' | null>(null);
  const plan = confirmTarget && emails.data ? planSend(emails.data.advisors, confirmTarget === 'all' ? undefined : confirmTarget) : null;
  const send = useMutation({
    mutationFn: (target: number[] | 'all') =>
      api.post('/api/emails', { ...selected!.end, ...(target === 'all' ? {} : { advisorIds: target }) }),
    onSuccess: () => {
      setConfirmTarget(null);
      refreshEmails();
    },
  });
  const stop = useMutation({ mutationFn: () => api.post('/api/emails/cancel', selected!.end), onSuccess: refreshEmails });
  const retry = useMutation({ mutationFn: (emailId: number) => api.post(`/api/emails/${emailId}/retry`, {}), onSuccess: refreshEmails });
  const askToSend = (target: number[] | 'all') => {
    send.reset();
    setConfirmTarget(target);
  };
  const showPreview = (advisorId: number) => {
    setPreviewId(advisorId);
    document.getElementById('vista-previa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const header = (
    <PageHeader
      title="Informes trimestrales"
      subtitle="Genera los PDF de todos los asesores con el diseño aprobado: balance del Art. 2.8, KPIs, insights y comparativa anónima con el equipo."
    />
  );

  if (quarters.isPending) return (<>{header}<LoadingBlock /></>);
  if (quarters.isError) return (<>{header}<ErrorBlock error={quarters.error} /></>);
  if (!selected) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon={CalendarX2}
            title="Todavía no hay ningún trimestre con datos suficientes"
            description="Hace falta actividad registrada en los dos últimos meses del trimestre (en «Datos mensuales»)."
          />
        </Card>
      </>
    );
  }

  const previousRuns = runs.data?.filter((run) => samePeriod({ year: run.periodEndYear, month: run.periodEndMonth }, selected.end)) ?? [];
  const lastRun = previousRuns[0];

  return (
    <>
      {header}
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <StepTitle number={1} title="Elige el trimestre">
                Pulsa el mes con el que empieza: el informe abarca ese mes y los dos siguientes.
              </StepTitle>
              <div className="mt-5 flex flex-col gap-6 sm:flex-row sm:items-start">
                <QuarterPicker
                  quarters={quarters.data}
                  selected={selected}
                  onSelect={(q) => setSelectedKey(startKey(q.start))}
                />
                <div className="hidden w-px self-stretch bg-border sm:block" />
                <QuarterHeading quarter={selected} summary={currentSummary} />
              </div>
              <div className="mt-6 border-t border-border pt-5">
                <QuarterTimeline summary={currentSummary} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <StepTitle number={2} title="Revisa los datos antes de generar">
                Lo que falta en «Datos mensuales» y cómo afecta a cada PDF.
              </StepTitle>
              <div className="mt-4">
                {summary.isError ? <ErrorBlock error={summary.error} /> : <DataReview summary={currentSummary} />}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <StepTitle number={3} title="Genera los informes">
                Un PDF por asesor, todos en un ZIP que se guarda en el historial.
              </StepTitle>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button size="lg" icon={Download} loading={generate.isPending} onClick={() => generate.mutate(selected)}>
                  {generate.isPending
                    ? `Generando ${currentSummary?.advisorCount ?? ''} informes…`
                    : `Generar todos (ZIP) · ${quarterLabel(selected)}`}
                </Button>
                {generate.isPending && <span className="text-xs text-muted-foreground">Suele tardar unos 20 segundos.</span>}
              </div>
              {lastRun && !generate.isPending && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Ya generado {previousRuns.length === 1 ? 'una vez' : `${previousRuns.length} veces`}: el último el{' '}
                  {formatDateTime(lastRun.createdAt)}
                  {lastRun.createdBy && ` por ${lastRun.createdBy}`}.{' '}
                  <Link href={`/api/reports/${lastRun.id}/download`}>Descargar ese ZIP</Link>
                </p>
              )}
              {generate.isError && (
                <Alert tone="danger" title="No se han generado los informes" className="mt-3">
                  {describeApiError(generate.error, 'No se han podido generar los informes.', {
                    409: 'Ya hay una generación de informes en marcha. Espera a que termine y vuelve a intentarlo.',
                  })}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <StepTitle number={4} title="Envía los informes por correo">
                Cada asesor recibe solo su informe en PDF{emails.data?.enabled ? `, desde ${emails.data.from}` : ''}. También puedes enviárselo a uno desde la vista previa.
              </StepTitle>
              <div className="mt-4">
                {emails.isError ? (
                  <ErrorBlock error={emails.error} />
                ) : (
                  <EmailPanel
                    overview={emails.data}
                    onSendAll={() => askToSend('all')}
                    onStop={() => stop.mutate()}
                    onRetry={(emailId) => retry.mutate(emailId)}
                    onPreview={showPreview}
                    stopping={stop.isPending}
                    retryingId={retry.isPending ? retry.variables : undefined}
                  />
                )}
                {(stop.isError || retry.isError) && (
                  <Alert tone="danger" title="No se ha completado la acción" className="mt-3">
                    {describeApiError(stop.error ?? retry.error, 'No se ha podido completar la acción.')}
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <AdvisorPreview
            quarter={selected}
            summary={currentSummary}
            chosenId={previewId}
            onChoose={setPreviewId}
            emailInfo={(id) => (emails.data?.enabled ? (emails.data.advisors.find((a) => a.id === id) ?? null) : null)}
            onSendEmail={(id) => askToSend([id])}
          />
          <RunHistory runs={runs.data} error={runs.isError ? runs.error : null} selected={selected} />
        </div>
      </div>
      <SendConfirmDialog
        open={confirmTarget !== null}
        plan={plan}
        quarter={quarterLabel(selected)}
        intervalSeconds={emails.data?.intervalSeconds ?? 4}
        from={emails.data?.from ?? ''}
        redirectTo={emails.data?.redirectTo ?? null}
        sending={send.isPending}
        error={
          send.isError
            ? describeApiError(send.error, 'No se ha podido enviar.', {
                503: 'El envío por correo no está activado en este entorno.',
                422: 'No hay nadie a quien enviar: no tienen email o ya están en la cola.',
              })
            : null
        }
        onConfirm={() => send.mutate(confirmTarget!)}
        onClose={() => setConfirmTarget(null)}
      />
    </>
  );
}

function StepTitle({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
        {number}
      </span>
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}
